/**
 * `/config` redirect — phase 41 follow-up.
 *
 * Phase 41 shipped /config as a multi-studio overview page (showing
 * file paths + raw YAML + parsed shape per studio) AND /config/:slug as
 * the per-studio editor. Mario asked to collapse the overview: he
 * doesn't want to see file paths or raw YAML, just the editor.
 *
 * This component resolves the active studio's slug from /api/whoami
 * and redirects to /config/<slug>. The studio is the one this tab is
 * mounted in — the `/s/<slug>/` prefix the server validated for this
 * request (staff-person-login-studio-switcher, 2026-09-27) — so a person
 * who is staff of several studios edits the config of the one they are
 * looking at. `studio: null` (no studio selected) cannot reach this page,
 * because the layout renders the chooser instead of any page; it is still
 * handled, by sending the tab to `/` where the layout decides again.
 *
 * The query string is carried through, so `/config?section=<id>` opens that
 * section of the editor (2026-10-21: the member page's Session feedback help
 * line links to Marketing flows this way).
 *
 * No props. Loading state is brief (one cached /api/whoami round-trip).
 */

import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { api, ApiError } from "../lib/api";

export function ConfigRedirect(): JSX.Element {
  const { search } = useLocation();
  const [slug, setSlug] = useState<string | null>(null);
  const [unscoped, setUnscoped] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void api
      .whoami()
      .then((data) => {
        if (cancelled) return;
        if (data.studio === null) {
          setUnscoped(true);
          return;
        }
        setSlug(data.studio.slug);
      })
      .catch((e: Error) => {
        if (cancelled) return;
        if (e instanceof ApiError) {
          setError(`api_error:${e.status}${e.code ? `:${e.code}` : ""}`);
        } else {
          setError(e.message);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (unscoped) {
    return <Navigate to="/" replace />;
  }
  if (error !== null) {
    return (
      <div className="page-studio-config">
        <h1>Studio config</h1>
        <div className="tag tag-danger">Failed to resolve studio: {error}</div>
      </div>
    );
  }
  if (slug === null) {
    return (
      <div className="page-studio-config">
        <h1>Studio config</h1>
        <p className="subtle">Loading…</p>
      </div>
    );
  }
  return (
    <Navigate to={`/config/${encodeURIComponent(slug)}${search}`} replace />
  );
}
